import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as classesService from "../services/classes";
import * as homeworksService from "../services/homeworks";
import * as studentsService from "../services/students";
import {
  COMMON_EXERCISE_TYPES,
  COMMON_TYPE_VALUES,
  EXERCISE_TYPE_ICONS,
  EXERCISE_TYPES,
  ExerciseConfigEditor,
  ExerciseLivePreview,
  buildDefaultConfig,
  exerciseProblems,
} from "./ProgramTestEditor";
import { toLocalInputValue } from "../utils/homeworkDates";
import "./ProgramTestEditor.css";
import "./ProgramTestTake.css";
import "./HomeworkEditor.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

const ALL_TYPES = EXERCISE_TYPES.map((x) => x.value);

// Same question editor as the tests: cards, type picker, autosave.
export default function HomeworkEditor() {
  const { homeworkId } = useParams();
  const { accessToken, effectiveRole } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";
  const tr = useCallback((key, options) => t(`homeworkUi.${key}`, options), [t]);
  const te = useCallback(
    (key, defaultValue, options) => t(`testsModule.editor.${key}`, { defaultValue, ...options }),
    [t],
  );

  const isTeacherOrAdmin = ["teacher", "admin", "superadmin"].includes(effectiveRole);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [homework, setHomework] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    due_at: "",
    status: "draft",
    target: "class",
    targetId: "",
  });
  const latestForm = useRef(form);

  const [exercises, setExercises] = useState([]);
  const latestDrafts = useRef(new Map());
  const saveTimers = useRef(new Map());
  const [saveStatus, setSaveStatus] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [publishProblems, setPublishProblems] = useState(null);

  const [newType, setNewType] = useState("multiple_choice");
  const [newPrompt, setNewPrompt] = useState("");
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const isAdmin = ["admin", "superadmin"].includes(effectiveRole);
        const [hw, classesRes, studentsRes] = await Promise.all([
          homeworksService.fetchHomework({
            accessToken,
            homeworkId,
            params: { include_exercises: "true" },
          }),
          classesService
            .fetchClasses({ accessToken, params: { page: 1 } })
            .catch(() => ({ classes: [] })),
          isAdmin
            ? studentsService.fetchStudents({ accessToken }).catch(() => ({ students: [] }))
            : { students: [] },
        ]);
        if (ignore) return;
        setHomework(hw);
        const classId = asArray(hw.assigned_class_group_ids)[0];
        const studentId = asArray(hw.assigned_student_ids)[0];
        const loaded = {
          title: hw.title || "",
          description: hw.description || "",
          due_at: toLocalInputValue(hw.due_at),
          status: hw.status || "draft",
          target: studentId && !classId ? "student" : "class",
          targetId: String(classId || studentId || ""),
        };
        latestForm.current = loaded;
        setForm(loaded);
        const list = asArray(hw.exercises);
        latestDrafts.current = new Map(list.map((e) => [e.id, e]));
        setExercises(list);
        setClasses(asArray(classesRes?.classes));
        setStudents(asArray(studentsRes?.students));
      } catch (e) {
        if (!ignore) setError(e?.message || "");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (accessToken && homeworkId) load();
    else setLoading(false);
    return () => {
      ignore = true;
    };
  }, [accessToken, effectiveRole, homeworkId]);

  const allStudents = useMemo(() => {
    const seen = new Map();
    classes.forEach((g) => asArray(g.students).forEach((s) => s?.id && seen.set(Number(s.id), s)));
    students.forEach((s) => s?.id && seen.set(Number(s.id), s));
    return [...seen.values()].sort((a, b) =>
      String(a.full_name || "").localeCompare(String(b.full_name || "")),
    );
  }, [classes, students]);

  const scheduleSave = useCallback((key, run, delay = 700) => {
    const timers = saveTimers.current;
    if (timers.has(key)) clearTimeout(timers.get(key));
    setSaveStatus((prev) => ({ ...prev, [key]: "pending" }));
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key);
        run();
      }, delay),
    );
  }, []);

  const saveDetails = useCallback(async () => {
    const f = latestForm.current;
    const id = f.targetId ? [Number(f.targetId)] : [];
    setSaveStatus((prev) => ({ ...prev, details: "saving" }));
    try {
      const updated = await homeworksService.updateHomework({
        accessToken,
        homeworkId,
        payload: {
          title: f.title,
          description: f.description,
          due_at: f.due_at || null,
          assigned_class_group_ids: f.target === "class" ? id : [],
          assigned_student_ids: f.target === "student" ? id : [],
        },
      });
      setHomework(updated);
      if (latestForm.current === f) setSaveStatus((prev) => ({ ...prev, details: "saved" }));
    } catch (e) {
      setSaveStatus((prev) => ({ ...prev, details: "error" }));
      setError(e?.message || "");
    }
  }, [accessToken, homeworkId]);

  const updateForm = (patch, { immediate = false } = {}) => {
    const next = { ...latestForm.current, ...patch };
    latestForm.current = next;
    setForm(next);
    scheduleSave("details", saveDetails, immediate ? 0 : 900);
  };

  const persistExercise = useCallback(
    async (exerciseId) => {
      const exercise = latestDrafts.current.get(exerciseId);
      if (!exercise) return;
      const key = `ex-${exerciseId}`;
      setSaveStatus((prev) => ({ ...prev, [key]: "saving" }));
      try {
        await homeworksService.updateHomeworkExercise({
          accessToken,
          exerciseId,
          payload: {
            order: exercise.order,
            points: exercise.points,
            prompt: exercise.prompt,
            config: exercise.config,
            exercise_type: exercise.exercise_type,
          },
        });
        if (latestDrafts.current.get(exerciseId) === exercise) {
          setSaveStatus((prev) => ({ ...prev, [key]: "saved" }));
        }
      } catch (e) {
        setSaveStatus((prev) => ({ ...prev, [key]: "error" }));
        setError(e?.message || "");
      }
    },
    [accessToken],
  );

  const updateExercise = useCallback(
    (next, { immediate = false } = {}) => {
      latestDrafts.current.set(next.id, next);
      setExercises((prev) => prev.map((e) => (e.id === next.id ? next : e)));
      scheduleSave(`ex-${next.id}`, () => persistExercise(next.id), immediate ? 0 : 700);
    },
    [persistExercise, scheduleSave],
  );

  // Flush pending saves when leaving the page.
  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      timers.forEach((timer, key) => {
        clearTimeout(timer);
        if (key.startsWith("ex-")) persistExercise(Number(key.slice(3)));
        if (key === "details") saveDetails();
      });
      timers.clear();
    };
  }, [persistExercise, saveDetails]);

  const hasUnsaved = Object.values(saveStatus).some((v) => v === "pending" || v === "saving");
  const hasSaveError = Object.values(saveStatus).includes("error");

  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsaved]);

  const sorted = useMemo(
    () => [...exercises].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [exercises],
  );

  const moveExercise = (exerciseId, delta) => {
    const list = sorted.slice();
    const index = list.findIndex((e) => e.id === exerciseId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    list.forEach((exercise, order) => {
      if (exercise.order !== order) updateExercise({ ...exercise, order }, { immediate: true });
    });
  };

  const deleteExercise = async (exerciseId) => {
    if (!window.confirm(te("confirmDeleteExercise", "Delete this question?"))) return;
    setBusyId(exerciseId);
    try {
      await homeworksService.deleteHomeworkExercise({ accessToken, exerciseId });
      latestDrafts.current.delete(exerciseId);
      setExercises((prev) => prev.filter((e) => e.id !== exerciseId));
    } catch (e) {
      setError(e?.message || "");
    } finally {
      setBusyId(null);
    }
  };

  const addExercise = async (event) => {
    event.preventDefault();
    setAdding(true);
    setError("");
    try {
      const created = await homeworksService.createHomeworkExercise({
        accessToken,
        homeworkId,
        payload: {
          exercise_type: newType,
          prompt: newPrompt,
          points: 1,
          config: buildDefaultConfig(newType),
          order: sorted.length,
        },
      });
      latestDrafts.current.set(created.id, created);
      setExercises((prev) => [...prev, created]);
      setNewPrompt("");
      setTimeout(() => {
        document
          .getElementById(`exercise-${created.id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } catch (e) {
      setError(e?.message || "");
    } finally {
      setAdding(false);
    }
  };

  const problemList = useMemo(() => {
    const items = [];
    if (!form.title.trim()) items.push({ text: tr("editor.noTitle") });
    if (!form.targetId) items.push({ text: tr("editor.noTarget") });
    if (!sorted.length) items.push({ text: te("problems.noQuestions", "Add at least one question.") });
    let n = 0;
    sorted.forEach((exercise) => {
      const isStimulus = exercise.exercise_type === "stimulus";
      if (!isStimulus) n += 1;
      exerciseProblems(exercise).forEach((key) =>
        items.push({
          exerciseId: exercise.id,
          number: isStimulus ? null : n,
          text: te(`problems.${key}`, key),
        }),
      );
    });
    return items;
  }, [form.targetId, form.title, sorted, te, tr]);

  const setStatus = async (status, { force = false } = {}) => {
    if (status === "published" && problemList.length && !force) {
      setPublishProblems(problemList);
      return;
    }
    setPublishProblems(null);
    setError("");
    try {
      const updated = await homeworksService.updateHomework({
        accessToken,
        homeworkId,
        payload: { status },
      });
      setHomework(updated);
      setForm((prev) => ({ ...prev, status: updated?.status || status }));
      latestForm.current = { ...latestForm.current, status: updated?.status || status };
    } catch (e) {
      setError(e?.message || "");
    }
  };

  if (!isTeacherOrAdmin) {
    return (
      <main className="test-editor" dir={dir}>
        <div className="container test-editor__container">
          <div className="test-editor__error">
            {t("testsModule.common.notAllowed", { defaultValue: "Not allowed" })}
          </div>
        </div>
      </main>
    );
  }

  const published = form.status === "published";
  const statusText =
    form.status === "published"
      ? tr("editor.statusPublished")
      : form.status === "archived"
        ? tr("editor.statusArchived")
        : tr("editor.statusDraft");

  return (
    <main className="test-editor hw-editor" dir={dir}>
      <div className="container test-editor__container">
        <header className="test-editor__header">
          <div>
            <h1 className="test-editor__title">{tr("editor.title")}</h1>
            <p className="test-editor__subtitle">{form.title || homework?.title || ""}</p>
          </div>
          <span
            className={`test-editor__saveState${
              hasSaveError ? " is-error" : hasUnsaved ? " is-saving" : ""
            }`}
            role="status"
          >
            <i
              className={`fa-solid ${
                hasSaveError ? "fa-triangle-exclamation" : hasUnsaved ? "fa-rotate" : "fa-cloud"
              }`}
              aria-hidden="true"
            />
            {hasSaveError
              ? te("saveError", "Some changes were not saved")
              : hasUnsaved
                ? t("testsModule.common.saving", { defaultValue: "Saving..." })
                : te("allSaved", "All changes saved")}
          </span>
          <div className="test-editor__headerActions">
            <Link className="btn ghost" to="/homeworks">
              {t("testsModule.common.back", { defaultValue: "Back" })}
            </Link>
            <Link className="btn ghost" to={`/homeworks/${homeworkId}/submissions`}>
              {tr("copies")}
            </Link>
          </div>
        </header>

        {error ? <div className="test-editor__error">{error}</div> : null}
        {loading ? <div className="homeworks-page__spinner" aria-hidden="true" /> : null}

        {!loading && homework ? (
          <section className="test-editor__card">
            <h2 className="test-editor__sectionTitle">{tr("editor.details")}</h2>
            <div className="test-editor__grid">
              <label className="test-editor__full">
                <span>{tr("editor.name")}</span>
                <input value={form.title} onChange={(e) => updateForm({ title: e.target.value })} />
              </label>
              <label className="test-editor__full">
                <span>{tr("editor.instructions")}</span>
                <textarea
                  rows={3}
                  value={form.description}
                  placeholder={tr("editor.instructionsPlaceholder")}
                  onChange={(e) => updateForm({ description: e.target.value })}
                />
              </label>

              <div className="test-editor__full hw-editor__target">
                <span className="hw-editor__label">{tr("editor.for")}</span>
                <div className="hw-segment" role="radiogroup">
                  {["class", "student"].map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={form.target === value}
                      className={form.target === value ? "is-active" : ""}
                      onClick={() => updateForm({ target: value, targetId: "" }, { immediate: true })}
                    >
                      <i
                        className={`fa-solid ${value === "class" ? "fa-users" : "fa-user"}`}
                        aria-hidden="true"
                      />
                      {value === "class" ? tr("create.aClass") : tr("create.aStudent")}
                    </button>
                  ))}
                </div>
              </div>

              <label>
                <span>
                  {form.target === "class" ? tr("create.pickClass") : tr("create.pickStudent")}
                </span>
                <select
                  value={form.targetId}
                  onChange={(e) => updateForm({ targetId: e.target.value }, { immediate: true })}
                >
                  <option value="">
                    {form.target === "class"
                      ? classes.length
                        ? tr("create.pickClass")
                        : tr("create.noClasses")
                      : allStudents.length
                        ? tr("create.pickStudent")
                        : tr("create.noStudents")}
                  </option>
                  {(form.target === "class" ? classes : allStudents).map((item) => (
                    <option key={item.id} value={String(item.id)}>
                      {item.name || item.full_name || `#${item.id}`}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>{tr("editor.due")}</span>
                <input
                  type="datetime-local"
                  value={form.due_at}
                  onChange={(e) => updateForm({ due_at: e.target.value }, { immediate: true })}
                />
              </label>

              <div className="test-editor__publish">
                <span className={`test-editor__status ${published ? "is-published" : "is-draft"}`}>
                  <i
                    className={`fa-solid ${published ? "fa-circle-check" : "fa-pen-ruler"}`}
                    aria-hidden="true"
                  />
                  {statusText}
                </span>
                <div className="test-editor__actions">
                  <Link className="btn ghost" to={`/homeworks/${homeworkId}`}>
                    {tr("preview")}
                  </Link>
                  <button
                    type="button"
                    className={`btn ${published ? "ghost" : "primary"}`}
                    onClick={() => setStatus(published ? "draft" : "published")}
                    disabled={hasUnsaved}
                  >
                    {published ? tr("unpublish") : tr("publish")}
                  </button>
                </div>
              </div>

              {publishProblems ? (
                <div className="test-editor__problems" role="alert">
                  <strong>{tr("editor.problemsTitle")}</strong>
                  <ul>
                    {publishProblems.map((item, index) => (
                      <li key={`${item.exerciseId || "x"}-${index}`}>
                        {item.exerciseId ? (
                          <>
                            <button
                              type="button"
                              className="test-editor__problemLink"
                              onClick={() =>
                                document
                                  .getElementById(`exercise-${item.exerciseId}`)
                                  ?.scrollIntoView({ behavior: "smooth", block: "center" })
                              }
                            >
                              {item.number
                                ? te("questionN", "Question {{n}}", { n: item.number })
                                : te("typeNames.stimulus", "Text to read")}
                            </button>
                            {" — "}
                          </>
                        ) : null}
                        {item.text}
                      </li>
                    ))}
                  </ul>
                  <div className="test-editor__actions">
                    <button type="button" className="btn ghost" onClick={() => setPublishProblems(null)}>
                      {te("keepEditing", "Keep editing")}
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => setStatus("published", { force: true })}
                    >
                      {te("publishAnyway", "Publish anyway")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {!loading && homework ? (
          <section className="test-editor__card test-editor__card--exercises">
            <h2 className="test-editor__sectionTitle">{te("questionsTitle", "Questions")}</h2>

            <form className="test-editor__add" onSubmit={addExercise}>
              <div className="test-editor__full">
                <span className="test-editor__pickerLabel">
                  {te("chooseType", "1. Choose the type of question")}
                </span>
                <div className="test-editor__typePicker" role="radiogroup">
                  {ALL_TYPES.filter((v) => showMoreTypes || COMMON_TYPE_VALUES.has(v)).map((value) => {
                    const icon =
                      COMMON_EXERCISE_TYPES.find((x) => x.value === value)?.icon ||
                      EXERCISE_TYPE_ICONS[value] ||
                      "fa-circle-question";
                    const active = newType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`test-editor__typeCard${active ? " is-active" : ""}`}
                        onClick={() => setNewType(value)}
                        disabled={adding}
                      >
                        <i className={`fa-solid ${icon}`} aria-hidden="true" />
                        <strong>
                          {te(`typeNames.${value}`, value)}
                        </strong>
                        <small>{te(`typeHints.${value}`, "")}</small>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="test-editor__moreTypes"
                  onClick={() => setShowMoreTypes((v) => !v)}
                >
                  {showMoreTypes ? te("fewerTypes", "Fewer types") : te("moreTypes", "More types")}
                </button>
              </div>

              <label className="test-editor__full">
                <span>
                  {newType === "stimulus"
                    ? te("stimulusPromptLabel", "2. Paste the text students will read")
                    : te("questionPromptLabel", "2. Write the question")}
                </span>
                <textarea
                  rows={newType === "stimulus" ? 6 : 3}
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  disabled={adding}
                  required
                />
              </label>

              <div className="test-editor__actions">
                <button className="btn primary" type="submit" disabled={adding}>
                  {adding ? te("adding", "Adding...") : te("addQuestion", "Add the question")}
                </button>
              </div>
            </form>

            <div className="test-editor__exerciseList">
              {sorted.map((exercise, index) => {
                const isStimulus = exercise.exercise_type === "stimulus";
                const number = sorted
                  .slice(0, index + 1)
                  .filter((e) => e.exercise_type !== "stimulus").length;
                const problems = exerciseProblems(exercise);
                return (
                  <article
                    className={`test-editor__exercise${isStimulus ? " test-editor__exercise--stimulus" : ""}`}
                    key={exercise.id}
                    id={`exercise-${exercise.id}`}
                  >
                    <header className="test-editor__exerciseHead">
                      <div className="test-editor__exerciseHeadTitle">
                        <strong>
                          {isStimulus
                            ? te("typeNames.stimulus", "Text to read")
                            : te("questionN", "Question {{n}}", { n: number })}
                        </strong>
                        <span className="test-editor__pill">
                          {te(`typeNames.${exercise.exercise_type}`, exercise.exercise_type)}
                        </span>
                        {saveStatus[`ex-${exercise.id}`] === "error" ? (
                          <span className="test-editor__pill test-editor__pill--error">
                            {te("notSaved", "Not saved")}
                          </span>
                        ) : null}
                      </div>
                      <div className="test-editor__exerciseHeadActions">
                        {!isStimulus ? (
                          <label className="test-editor__points">
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={String(exercise.points ?? 1)}
                              onChange={(e) => {
                                const num = parseInt(e.target.value, 10);
                                updateExercise({
                                  ...exercise,
                                  points: Number.isNaN(num) ? 1 : Math.max(1, num),
                                });
                              }}
                              aria-label={te("points", "Points")}
                            />
                            <span>{te("pointsAbbrev", "pts")}</span>
                          </label>
                        ) : null}
                        <button
                          type="button"
                          className="test-editor__iconBtn"
                          onClick={() => moveExercise(exercise.id, -1)}
                          disabled={index === 0}
                          aria-label={t("testsModule.runner.moveUp", { defaultValue: "Move up" })}
                        >
                          <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="test-editor__iconBtn"
                          onClick={() => moveExercise(exercise.id, 1)}
                          disabled={index === sorted.length - 1}
                          aria-label={t("testsModule.runner.moveDown", { defaultValue: "Move down" })}
                        >
                          <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="test-editor__iconBtn test-editor__iconBtn--danger"
                          onClick={() => deleteExercise(exercise.id)}
                          disabled={busyId === exercise.id}
                          aria-label={tr("delete")}
                        >
                          <i className="fa-solid fa-trash" aria-hidden="true" />
                        </button>
                      </div>
                    </header>

                    <label className="test-editor__full test-editor__prompt">
                      <span>
                        {isStimulus ? te("stimulusText", "Text") : te("questionText", "Question")}
                      </span>
                      <textarea
                        rows={isStimulus ? 6 : 2}
                        value={exercise.prompt || ""}
                        onChange={(e) => updateExercise({ ...exercise, prompt: e.target.value })}
                      />
                    </label>

                    <ExerciseConfigEditor exercise={exercise} onChange={(next) => updateExercise(next)} />

                    {problems.length ? (
                      <ul className="test-editor__warnings">
                        {problems.map((key) => (
                          <li key={key}>
                            <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                            {te(`problems.${key}`, key)}
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <details className="test-editor__preview">
                      <summary>{te("livePreview", "Live preview")}</summary>
                      <ExerciseLivePreview exercise={exercise} language={language} />
                    </details>
                  </article>
                );
              })}

              {!sorted.length ? (
                <div className="test-editor__empty">
                  {te("noExercisesYet", "No questions yet.")}
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}
